import MarkdownIt from 'markdown-it';

const markdown = new MarkdownIt();

export const threatGroupSections = {
    summary: { id: 'threat-group-summary', title: 'Executive Summary' },
    profile: { id: 'threat-group-profile', title: 'Group Profile & Network' },
    leaders: { id: 'threat-group-leaders', title: 'Leadership' },
    asset: { id: 'threat-group-asset', title: 'Asset-Specific Risk Assessment' },
    pmesii: { id: 'threat-group-pmesii', title: 'PMESII-PT Analysis' },
    ascope: { id: 'threat-group-ascope', title: 'Civil Considerations (ASCOPE)' },
    carver: { id: 'threat-group-carver', title: 'CARVER Assessment' },
    outlook: { id: 'threat-group-outlook', title: 'Risk Assessment & Strategic Outlook' },
    factors: { id: 'threat-group-factors', title: 'Threat Risk Factors' },
    scenarios: { id: 'threat-group-scenarios', title: 'Courses of Action' },
    recommendations: { id: 'threat-group-recommendations', title: 'Recommendations & Next Steps' },
    methodology: { id: 'threat-group-methodology', title: 'Methodology, Confidence & Gaps' },
};

export const threatGroupAdditionalSection = (index: number) => `threat-group-additional-${index}`;

interface TextModule {
    path: string[];
    section: { id: string; title: string };
    text: string;
}

function narrativeModules(draft: any): TextModule[] {
    const modules: TextModule[] = [];
    const add = (path: string[], section: TextModule['section']) => {
        const value = path.reduce((entry, key) => entry?.[key], draft);
        if (typeof value === 'string' && value.trim())
            modules.push({ path, section, text: value.replace(/\\n/g, '\n').replace(/\r\n?/g, '\n') });
    };
    // The dedicated forecast and confidence modules own their paragraphs.
    add(['strategicOutlook'], threatGroupSections.outlook);
    add(['incidentAssessment'], threatGroupSections.outlook);
    for (let index = 0; index < (draft.clientQuestionAnswers?.length || 0); index++) {
        const answer = draft.clientQuestionAnswers[index];
        const section = { id: `threat-group-kiq-${answer.questionIndex}`, title: `Client Question ${answer.questionIndex}` };
        for (const field of ['answer', 'operationalImpact', 'evidenceGap'])
            add(['clientQuestionAnswers', String(index), field], section);
    }
    add(['confidenceJustification'], threatGroupSections.methodology);
    add(['analystConfidence', 'justification'], threatGroupSections.methodology);
    add(['analystConfidence', 'dataGaps'], threatGroupSections.methodology);
    add(['methodologyAndConfidence'], threatGroupSections.methodology);
    add(['riskSummary'], threatGroupSections.outlook);
    add([draft.summary ? 'summary' : 'description'], threatGroupSections.summary);
    add(['ideology'], threatGroupSections.profile);
    add(['historicalContext'], threatGroupSections.profile);
    for (let index = 0; index < (draft.keyLeaders?.length || 0); index++)
        add(['keyLeaders', String(index), 'bio'], threatGroupSections.leaders);
    add(['assetRiskAnalysis'], threatGroupSections.asset);
    const carver = draft.carverAssessment || draft.carver;
    for (const [field, section, keys] of [
        [draft.pmesiiAnalysis ? 'pmesiiAnalysis' : 'pmesii', threatGroupSections.pmesii,
            ['political', 'military', 'economic', 'social', 'infrastructure', 'information', 'physicalEnvironment', 'time']],
        [draft.carverAssessment ? 'carverAssessment' : 'carver', threatGroupSections.carver,
            ['criticality', 'accessibility', 'recuperability', 'vulnerability', 'effect', 'recognizability', carver?.overallAssessment ? 'overallAssessment' : 'analysis']],
        ['ascopeAnalysis', threatGroupSections.ascope, ['areas', 'structures', 'capabilities', 'organizations', 'people', 'events']],
    ] as const)
        for (const key of keys)
            add([field, key], section);
    for (let index = 0; index < (draft.riskFactors?.length || 0); index++)
        add(['riskFactors', String(index), 'rationale'], threatGroupSections.factors);
    for (const key of ['mlcoa', 'mdcoa', 'bestCase'])
        add(['scenarios', key, 'description'], threatGroupSections.scenarios);
    for (let index = 0; index < (draft.recommendations?.length || 0); index++)
        add(['recommendations', String(index)], threatGroupSections.recommendations);
    for (let index = 0; index < (draft.additionalSections?.length || 0); index++)
        add(['additionalSections', String(index), 'content'], {
            id: threatGroupAdditionalSection(index),
            title: String(draft.additionalSections[index]?.title || draft.additionalSections[index]?.heading || 'Actor Intelligence'),
        });
    return modules;
}

function replaceText(value: any, path: string[], text: string): any {
    if (!path.length)
        return text;
    const copy = Array.isArray(value) ? [...value] : { ...value };
    copy[path[0]] = replaceText(value[path[0]], path.slice(1), text);
    return copy;
}

function scanParagraphs<T>(draft: T, replace: boolean) {
    let report = draft;
    const duplicates: string[] = [];
    const seen = new Map<string, TextModule>();
    if (!draft || typeof draft !== 'object' || Array.isArray(draft))
        return { report, duplicates };
    for (const module of narrativeModules(draft)) {
        const lines = module.text.split('\n');
        const replacements: { start: number; end: number; text: string }[] = [];
        const tokens = markdown.parse(module.text, {});
        for (let index = 0; index < tokens.length; index++) {
            const token = tokens[index];
            if (token.type !== 'paragraph_open' || !token.map)
                continue;
            const children = tokens[index + 1]?.children || [];
            if (/^\[See .+\]\(#threat-group-[a-z0-9-]+\)\.$/.test(tokens[index + 1]?.content || ''))
                continue;
            const text = children.map(child => child.type === 'softbreak' || child.type === 'hardbreak' ? ' ' : child.content)
                .join('').normalize('NFKC').replace(/\s+/g, ' ').trim().toLowerCase();
            const links = children.filter(child => child.type === 'link_open').map(child => child.attrGet('href'));
            const key = text ? JSON.stringify([text, links]) : '';
            if (!key)
                continue;
            const original = seen.get(key);
            if (!original) {
                seen.set(key, module);
                continue;
            }
            duplicates.push(`${module.path.join('.')} repeats a paragraph from ${original.path.join('.')}`);
            const prefix = lines[token.map[0]].match(/^(\s*(?:>\s*)*(?:(?:[-+*]|\d+[.)])\s+)?)/)?.[0] || '';
            const title = original.section.title.replace(/[\\[\]]/g, '\\$&');
            replacements.push({ start: token.map[0], end: token.map[1],
                text: `${prefix}[See ${title}](#${original.section.id}).` });
        }
        if (replace && replacements.length) {
            for (let index = replacements.length - 1; index >= 0; index--) {
                const replacement = replacements[index];
                lines.splice(replacement.start, replacement.end - replacement.start, replacement.text);
            }
            report = replaceText(report, module.path, lines.join('\n'));
        }
    }
    return { report, duplicates };
}

export const threatGroupDuplicateParagraphs = (draft: unknown): string[] => scanParagraphs(draft, false).duplicates;

export const prepareThreatGroupReport = <T>(draft: T): T => scanParagraphs(draft, true).report;
